import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Button from "@/components/Button/Button";
import { Select } from "@/components/FormControl/Select/Select";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import type { StatusTone } from "@/components/StatusBadge/StatusBadge";
import ArrowIcon from "@/components/SVGComponents/ArrowIcon";
import CheckIcon from "@/components/SVGComponents/CheckIcon";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import { formatDate } from "@/lib/dateFormat";
import { getDisplayName, getInitials } from "@/lib/user";
import toast from "@/lib/toast";
import routes from "@/routes/routes";
import userService from "@/services/api/user";
import type { User, UserAnswer, UserCategory } from "@/services/api/user";
import styles from "./ApplicationReview.module.scss";

const DEFAULT_USER_CATEGORY: { label: string; value: UserCategory } = { label: "Chartered", value: "CHARTERED" };
const USER_CATEGORY_OPTIONS: { label: string; value: UserCategory }[] = [
  DEFAULT_USER_CATEGORY,
  { label: "Unchartered", value: "UNCHARTERED" },
];

const getStatusTone = (status: string): StatusTone => {
  const normalized = status.toLowerCase();
  if (["approved", "active"].includes(normalized)) return "success";
  if (["shortlisted", "under_review", "pending"].includes(normalized)) return "warning";
  if (["rejected", "blocked", "banned", "block"].includes(normalized)) return "danger";
  return "neutral";
};

const hasIdentifiableApplication = (candidate: User | undefined | null, memberId: string | undefined): candidate is User =>
  Boolean(candidate && typeof candidate.id === "number" && String(candidate.id) === memberId);

const formatSubmittedDate = (iso: string | undefined | null) =>
  formatDate(iso, { locale: navigator.language, month: "long", day: "numeric", year: "numeric" });

const formatSubmittedRelative = (iso: string | undefined | null) => {
  if (!iso) return "submission date unavailable";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "submission date unavailable";
  const days = Math.max(0, Math.round((Date.now() - date.getTime()) / 86_400_000));
  if (days === 0) return "submitted today";
  if (days === 1) return "submitted 1 day ago";
  return `submitted ${days} days ago`;
};

const ApplicationReview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { memberId } = useParams<{ memberId: string }>();
  const [userCategory, setUserCategory] = useState(DEFAULT_USER_CATEGORY);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const goToWaitlist = () => navigate(routes.privateRoutes.WAITLIST.path);

  const row = location.state as User | undefined;
  const hasValidApplication = hasIdentifiableApplication(row, memberId);

  const application =
    hasValidApplication && row
      ? {
          name: getDisplayName(row),
          userType: row.userType || "—",
          companyName: row.companyName?.trim() || "—",
          status: row.status || "—",
          email: row.email || "—",
          mobile: row.mobileNo || "—",
          location: row.location || "—",
          submittedDate: formatSubmittedDate(row.createdAt),
          submittedRelative: formatSubmittedRelative(row.createdAt),
        }
      : null;

  usePageSubtitle(application ? `Waitlist › ${application.name} · ${application.submittedRelative}` : null);

  const [responses, setResponses] = useState<UserAnswer[]>([]);
  const [isLoadingResponses, setIsLoadingResponses] = useState(false);
  const [responsesError, setResponsesError] = useState<string | null>(null);

  const fetchResponses = useCallback(async () => {
    if (!hasValidApplication || !row) return;
    setIsLoadingResponses(true);
    setResponsesError(null);
    try {
      const result = await userService.getUserAnswers(row.id);
      if (!Array.isArray(result?.data)) {
        throw new Error("Unexpected /answers response shape");
      }
      setResponses(result.data.filter((item): item is UserAnswer => Boolean(item) && typeof item.questionTitle === "string"));
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — this message is just for the inline
      // retry state below.
      setResponsesError("Couldn't load this application's responses.");
    } finally {
      setIsLoadingResponses(false);
    }
  }, [hasValidApplication, row]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  if (!application || !row) {
    return (
      <div className={styles.page}>
        <button type="button" className={styles.backLink} onClick={goToWaitlist}>
          <ArrowIcon className={styles.backIcon} />
          Back to Waitlist
        </button>
        <div className={styles.errorCard}>
          <h2 className={styles.errorTitle}>This application isn't available</h2>
          <p className={styles.errorBody}>
            Open it from the Waitlist list — a direct link or a refreshed page can't load an application's details on
            its own (no lookup-by-id endpoint exists yet).
          </p>
          <Button onClick={goToWaitlist}>BACK TO WAITLIST</Button>
        </div>
      </div>
    );
  }

  const updateStatus = async (status: "APPROVED" | "REJECTED", successMessage: string) => {
    setIsSubmitting(true);
    try {
      await userService.updateUserStatus(row.id, {
        status,
        statusRemark: "",
        userCategory: userCategory.value,
      });
      toast.success(successMessage);
      goToWaitlist();
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <button type="button" className={styles.backLink} onClick={goToWaitlist}>
        <ArrowIcon className={styles.backIcon} />
        Back to Waitlist
      </button>

      <div className={styles.layout}>
        <div className={styles.mainColumn}>
          <div className={styles.candidateCard}>
            <div className={styles.avatar}>{getInitials(application.name)}</div>
            <div className={styles.candidateInfo}>
              <h1 className={styles.candidateName}>{application.name}</h1>
              <div className={styles.badgeRow}>
                {row.userType === "FOUNDER" && (
                  <StatusBadge label={application.companyName.toUpperCase()} tone="neutral" />
                )}
                <StatusBadge label={application.status.toUpperCase().replace("_"," ")} tone={getStatusTone(application.status)} />
              </div>
              <p className={styles.contactLine}>
                {application.email} · {application.mobile} · {application.location}
              </p>
            </div>
            <div className={styles.submittedInfo}>
              <span className={styles.submittedLabel}>Submitted</span>
              <span className={styles.submittedDate}>{application.submittedDate}</span>
            </div>
          </div>

          <div className={styles.responsesCard}>
            <div className={styles.responsesHeader}>
              <h2 className={styles.cardTitle}>Application Responses</h2>
            </div>

            {isLoadingResponses ? (
              <p className={styles.responsesStateText}>Loading responses…</p>
            ) : responsesError ? (
              <div className={styles.responsesStateText}>
                <p>{responsesError}</p>
                <Button outlined onClick={fetchResponses}>
                  TRY AGAIN
                </Button>
              </div>
            ) : responses.length === 0 ? (
              <p className={styles.responsesStateText}>No responses on file for this application.</p>
            ) : (
              <div className={styles.responsesList}>
                {responses.map((item, index) => (
                  <div className={styles.responseRow} key={index}>
                    <span className={styles.responseNumber}>{String(index + 1).padStart(2, "0")}</span>
                    <div className={styles.responseBody}>
                      <p className={styles.responseQuestion}>{item.questionTitle || "Untitled question"}</p>
                      <p className={styles.responseAnswer}>{item.answers?.value || item.answers?.answer || "—"}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.sideColumn}>
          <div className={styles.decisionCard}>
            <h2 className={styles.cardTitle}>Decision</h2>
            <Select
              name="userCategory"
              label="Category"
              labelVariant="caption"
              options={USER_CATEGORY_OPTIONS}
              value={userCategory}
              onChange={(option) => {
                if (option && !Array.isArray(option)) {
                  setUserCategory(option as typeof DEFAULT_USER_CATEGORY);
                }
              }}
              isDisabled={isSubmitting}
            />
            <Button
              startIcon={<CheckIcon width={16} height={16} />}
              disabled={isSubmitting}
              onClick={() => updateStatus("APPROVED", `${application.name} approved.`)}
            >
              APPROVE & SEND INVITE
            </Button>
            <Button
              outlined
              danger
              startIcon={<CrossIcon withBg={false} stroke="currentColor" strokeWidth={2} width={12} height={12} />}
              disabled={isSubmitting}
              onClick={() => updateStatus("REJECTED", `${application.name} declined.`)}
            >
              DECLINE APPLICATION
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationReview;
